import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Landing page for a magic-link click. Verifies token, sets reader
 * session cookie server-side, then redirects to /my/library.
 */
export default function MagicVerify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("Signing you in…");

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("Missing sign-in token."); return; }
    let cancelled = false;
    api.get(`/auth/magic-verify?token=${encodeURIComponent(token)}`)
      .then(async () => {
        if (cancelled) return;
        setStatus("ok");
        setMessage("Welcome back! Redirecting…");
        try { await refresh?.(); } catch { /* ignore */ }
        setTimeout(() => navigate("/my/library", { replace: true }), 700);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        setMessage(err.response?.data?.detail || "This sign-in link has expired or already been used.");
      });
    return () => { cancelled = true; };
  }, [token, navigate, refresh]);

  const icon = status === "ok" ? <CheckCircle2 size={30} />
    : status === "error" ? <XCircle size={30} />
    : <Loader2 size={30} className="animate-spin" />;
  const bg = status === "ok" ? "linear-gradient(135deg,#66D9A6 0%,#3FB07B 100%)"
    : status === "error" ? "linear-gradient(135deg,#FF6B9D 0%,#FF8C42 100%)"
    : "linear-gradient(135deg,#FFD93D 0%,#FF8C42 100%)";

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="magic-verify-page">
      <span
        className="inline-flex w-16 h-16 rounded-3xl items-center justify-center mb-6"
        style={{ background: bg, color: "#FFFFFF" }}
      >{icon}</span>
      <h1 className="font-fredoka text-4xl sm:text-5xl" style={{ color: "#2D3055" }}>
        {status === "ok" ? "You're in! ✨"
          : status === "error" ? "This link isn't working"
          : "Signing you in…"}
      </h1>
      <p className="mt-4 font-nunito text-lg" style={{ color: "#4A4E6D" }}>{message}</p>
    </div>
  );
}
